import type { ReactNode } from "react";
import { MapPin, Pencil, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataCard, DetailRow } from "@/components/ui/data-card";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { formatAddress } from "@/components/forms/AddressFields";
import type { Customer, Location } from "@/types/tms";

export type LocationCardLocation = Location & { customer?: Customer | null };

interface LocationCardProps {
  location: LocationCardLocation;
  displayName: string;
  isChecked: boolean;
  onToggle: (id: string) => void;
  onClick: (location: LocationCardLocation) => void;
  onEdit: (location: LocationCardLocation) => void;
  onDelete: (id: string) => void;
  /** Body copy for the confirm dialog (archive warning). */
  deleteDescription?: ReactNode;
  /** Fired when the confirm dialog opens/closes, for preflight lookups. */
  onDeleteOpenChange?: (open: boolean) => void;
  deleteTitle?: string;
  deleteConfirmLabel?: string;
}

function formatCoords(lat: number | null | undefined, lng: number | null | undefined) {
  return lat != null && lng != null ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : "—";
}

/** Mobile/tablet representation of a location row on the Locations page. */
export function LocationCard({
  location,
  displayName,
  isChecked,
  onToggle,
  onClick,
  onEdit,
  onDelete,
  deleteDescription,
  onDeleteOpenChange,
  deleteTitle,
  deleteConfirmLabel,
}: LocationCardProps) {
  return (
    <DataCard
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, input, a, [role="checkbox"]')) return;
        onClick(location);
      }}
    >
      <DataCard.Header>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => onToggle(location.id)}
            aria-label={`Select ${displayName}`}
          />
        </span>
        <MapPin className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium truncate">{displayName}</span>
        {location.track_in_yms ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            <Warehouse className="h-3 w-3" />
            YMS
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(location)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <ConfirmDeleteButton
            onConfirm={() => onDelete(location.id)}
            entityName="location"
            entityLabel={displayName}
            description={deleteDescription}
            onOpenChange={onDeleteOpenChange}
            title={deleteTitle}
            confirmLabel={deleteConfirmLabel}
          />
        </span>
      </DataCard.Header>

      <div className="divide-y divide-border">
        <DetailRow label="Customer" value={location.customer?.organization_name || null} />
        <DetailRow label="Address" value={formatAddress(location) || null} />
        <DetailRow
          label="Origin coords"
          value={
            <span className="font-mono text-xs">
              {formatCoords(location.origin_latitude, location.origin_longitude)}
            </span>
          }
        />
        <DetailRow
          label="Destination coords"
          value={
            <span className="font-mono text-xs">
              {formatCoords(location.destination_latitude, location.destination_longitude)}
            </span>
          }
        />
      </div>
    </DataCard>
  );
}